<template>
  <div
    id="business_information"
    class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova"
  >
    <div id="business_name" class="flex flex-col gap-1">
      <span>Business Name*</span>
      <a-input
        v-model:value="localFormData.businessName"
        class="input_medium"
        placeholder="Enter business name"
      />
    </div>

    <div id="registration_authority" class="flex flex-col gap-1">
      <span>Registration Authority*</span>
      <a-select
        v-model:value="localFormData.registrationAuthority"
        placeholder="Select registration authority"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4 opacity-60"
          />
        </template>

        <a-select-option
          v-for="(authority, index) in registrationAuthorityOptions"
          :key="`${authority?.id}_authority_${index}`"
          :value="authority.id"
        >
          {{ authority?.text }}
        </a-select-option>
      </a-select>
    </div>

    <div id="registration_number" class="flex flex-col gap-1">
      <span>Registration Number*</span>
      <a-input
        v-model:value="localFormData.registrationNumber"
        class="input_medium"
        placeholder="Enter registration number"
      />
    </div>

    <div id="business_website" class="flex flex-col gap-1">
      <span>Business Website*</span>
      <CustomInput
        format="text"
        v-model="localFormData.businessWebsite"
        placeholder="Enter business website"
        type="primary"
        :isError="websiteError"
        errorMessage="Please enter a valid website"
        :onChange="handleWebsiteChange"
      />
    </div>

    <a-button
      class="primary_medium self-end"
      :disabled="!isStepValid"
      :loading="submitting"
      @click="handleContinue"
    >
      {{ submitting ? "Continuing" : "Continue" }}
    </a-button>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { message } from "ant-design-vue";
import { capitalizeFirstLetter, sanitizeInput } from "@/src/utils/utils.js";
import { registrationAuthorityOptions } from "@/src/constants/phoneNumberConstants.js";

import CallService from "@/src/api/CallService.js";
import CustomInput from "@/src/components/CustomInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update:formData", "next-step"]);

const localFormData = ref({ ...props.formData });
const submitting = ref(false);
const websiteError = ref(false);

const isStepValid = computed(() => {
  return Boolean(
    localFormData.value.businessName &&
      localFormData.value.registrationAuthority &&
      localFormData.value.registrationNumber &&
      localFormData.value.businessWebsite,
  );
});

const handleWebsiteChange = () => {
  try {
    websiteError.value = false;
  } catch (error) {
    console.error("An error occurred while handling website change: ", error);
  }
};

const validateForm = () => {
  try {
    let isValid = true;

    if (
      !localFormData.value.businessName ||
      !localFormData.value.registrationAuthority ||
      !localFormData.value.registrationNumber
    ) {
      isValid = false;
    }

    if (!localFormData.value.businessWebsite) {
      websiteError.value = true;
      isValid = false;
    } else {
      const urlRegex =
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
      if (!urlRegex.test(localFormData.value.businessWebsite)) {
        websiteError.value = true;
        isValid = false;
      } else if (
        sanitizeInput(localFormData.value.businessWebsite) !==
        localFormData.value.businessWebsite
      ) {
        websiteError.value = true;
        isValid = false;
      } else {
        websiteError.value = false;
      }
    }

    return isValid;
  } catch (error) {
    console.error("An error occurred while validating form: ", error);
    return false;
  }
};

const toCreateEndUser = async () => {
  try {
    submitting.value = true;

    const payload = {
      business_name: localFormData.value?.businessName,
      business_registration_identifier:
        localFormData.value?.registrationAuthority,
      business_registration_number: localFormData.value?.registrationNumber,
      business_website: localFormData.value?.businessWebsite,
      country_code: localFormData.value?.countryCode || "",
    };

    const response = await CallService.createEndUser(payload);
    if (response?.success) {
      message.success(`Business Information stored successfully!`);
      emit("update:formData", { ...localFormData.value, infoID: response?.id });
      return true;
    }

    message.error("Failed to store business information");
    return false;
  } catch (error) {
    console.error("An error occurred while creating end user: ", error);
    message.error(
      capitalizeFirstLetter(
        error?.response?.data?.error ||
          "An error occurred while storing business information",
      ),
    );
    return false;
  } finally {
    submitting.value = false;
  }
};

const handleContinue = async () => {
  try {
    if (!validateForm() || submitting.value) return;

    emit("update:formData", { ...localFormData.value });

    const success = await toCreateEndUser();
    if (success) {
      console.log("Emitting next-step event");
      emit("next-step");
    }
  } catch (error) {
    console.error("An error occurred while handling continue: ", error);
  }
};

watch(
  () => props.formData,
  (newVal) => {
    localFormData.value = { ...newVal };
  },
  { deep: true },
);
</script>
